import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { getNextPageParam, getPagesContent, useGetUserFantasyWishlists, useGetUserTeams } from '@pkg/hooks';

import { type Competition, Period } from '@app/client/common/structs';
import { COMPETITIONS_GAME_CACHE } from '@app/client/resources/competitions';
import { callGetCompetitions } from '@app/client/resources/competitions/competitions';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

export const useFantasyGamesData = (period = Period.Present, disabled?: boolean, onError?: (err: string) => void) => {
    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);

    const userWishlists = useGetUserFantasyWishlists(!isAuthenticated || disabled || period === Period.Future);
    useGetUserTeams(disabled);
    const cache = [COMPETITIONS_GAME_CACHE, `${period}-game`];

    const authSettings = !isAuthenticated
        ? {
              credentials: 'omit' as RequestCredentials,
              ignoreHeaders: ['Authorization'],
          }
        : {};

    const {
        data: competitionData,
        isLoading,
        isFetching,
        ...rest
    } = useInfiniteQuery(
        cache,
        ({ pageParam = 0, signal }) =>
            callGetCompetitions({
                query: { period, page: pageParam || 0 },
                settings: { signal, ...authSettings },
            })
                .then((res) => {
                    if (!res) {
                        onError?.('Error getting competitions');
                    }
                    return res;
                })
                .catch((e) => {
                    onError?.(e);
                    return e;
                }),
        {
            enabled: !disabled,
            getNextPageParam,
            retry: false,
            keepPreviousData: true,
        },
    );

    const competitionPageContent = getPagesContent(competitionData?.pages);
    const comps = useMemo(() => {
        return (
            competitionPageContent?.map((comp) => {
                const foundWishlists = userWishlists?.filter((wishlist) => comp?.id === wishlist?.competition?.id);
                if (foundWishlists) {
                    return { ...comp, wishlists: foundWishlists };
                }
                return comp;
            }) || ([] as Competition[])
        );
    }, [competitionPageContent, userWishlists]);

    return { competitions: comps, isLoading, isFetching, ...rest };
};
